import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, Observable, of, tap } from 'rxjs';
import { IUser, IUserSettings, Roles } from '@shs-digi-care-monorepo/interfaces';
import { UserActions } from './user.actions';
import { UserService } from '../../services';
import { isInternal, isTaskOwner } from '../../util';

export interface UserStateModel {
    me: IUser | null;
    loading: boolean;
    loaded: boolean;
    error: string | null;
    users: {
        data: IUser[];
        page: number;
        pageSize: number;
        total: number;
        loading: boolean;
    };
    selectedUser: IUser | null;
    settings: IUserSettings | null;
    settingsLoading: boolean;
}

const defaults: UserStateModel = {
    me: null,
    loading: false,
    loaded: false,
    error: null,
    users: {
        data: [],
        page: 0,
        pageSize: 20,
        total: 0,
        loading: false
    },
    selectedUser: null,
    settings: null,
    settingsLoading: false
};

@State<UserStateModel>({
    name: 'userState',
    defaults
})
@Injectable()
export class UserState {
    private readonly userService = inject(UserService);

    // ============================================
    // SELECTORS
    // ============================================

    /**
     * Returns the current authenticated user.
     */
    @Selector()
    public static me(state: UserStateModel): IUser | null {
        return state.me;
    }

    /**
     * Returns whether the current user is an admin.
     */
    @Selector()
    public static isAdmin(state: UserStateModel): boolean {
        return state.me?.roles?.includes(Roles.ADMIN) ?? false;
    }

    /**
     * Returns whether the current user is a task owner.
     */
    @Selector()
    public static isTaskOwner(state: UserStateModel): boolean {
        return isTaskOwner(state.me);
    }

    /**
     * Returns whether the current user is an internal user.
     */
    @Selector()
    public static isInternal(state: UserStateModel): boolean {
        return isInternal(state.me);
    }

    /**
     * Returns whether the user is currently loading.
     */
    @Selector()
    public static loading(state: UserStateModel): boolean {
        return state.loading;
    }

    /**
     * Returns whether the user has been loaded.
     */
    @Selector()
    public static loaded(state: UserStateModel): boolean {
        return state.loaded;
    }

    /**
     * Returns the list of users.
     */
    @Selector()
    public static users(state: UserStateModel): IUser[] {
        return state.users.data;
    }

    /**
     * Returns users loading state.
     */
    @Selector()
    public static usersLoading(state: UserStateModel): boolean {
        return state.users.loading;
    }

    /**
     * Returns users pagination info.
     */
    @Selector()
    public static usersPagination(state: UserStateModel): { page: number; pageSize: number; total: number } {
        return {
            page: state.users.page,
            pageSize: state.users.pageSize,
            total: state.users.total
        };
    }

    /**
     * Returns the currently selected user.
     */
    @Selector()
    public static selectedUser(state: UserStateModel): IUser | null {
        return state.selectedUser;
    }

    /**
     * Returns any error that occurred during loading.
     */
    @Selector()
    public static error(state: UserStateModel): string | null {
        return state.error;
    }

    /**
     * Returns the current user's settings.
     */
    @Selector()
    public static settings(state: UserStateModel): IUserSettings | null {
        return state.settings;
    }

    /**
     * Returns whether the settings are currently loading.
     */
    @Selector()
    public static settingsLoading(state: UserStateModel): boolean {
        return state.settingsLoading;
    }

    // ============================================
    // ACTIONS
    // ============================================

    /**
     * Loads the current authenticated user.
     */
    @Action(UserActions.LoadMe)
    public loadMe(ctx: StateContext<UserStateModel>): Observable<unknown> {
        const state = ctx.getState();

        // Skip if already loaded or currently loading
        if (state.loaded || state.loading) {
            return of(null);
        }

        ctx.patchState({ loading: true, error: null });

        return this.userService.getMe().pipe(
            tap((user) => {
                ctx.patchState({
                    me: user ?? null,
                    loading: false,
                    loaded: true
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: false,
                    error: error.message || 'Failed to load user'
                });
                return of(null);
            })
        );
    }

    /**
     * Sets the current user directly.
     */
    @Action(UserActions.SetMe)
    public setMe(ctx: StateContext<UserStateModel>, { user }: UserActions.SetMe): void {
        ctx.patchState({
            me: user,
            loaded: true
        });
    }

    /**
     * Clears the current user from the store.
     */
    @Action(UserActions.Clear)
    public clear(ctx: StateContext<UserStateModel>): void {
        ctx.patchState({
            me: null,
            loaded: false,
            error: null
        });
    }

    /**
     * Loads users with pagination and filters.
     */
    @Action(UserActions.LoadUsers)
    public loadUsers(ctx: StateContext<UserStateModel>, { payload }: UserActions.LoadUsers): Observable<unknown> {
        ctx.patchState({
            users: { ...ctx.getState().users, loading: true }
        });

        return this.userService
            .getUsers(
                payload.page,
                payload.pageSize,
                payload.searchTerm,
                payload.roles,
                payload.teamId,
                payload.availableFrom,
                payload.availableTo
            )
            .pipe(
                tap((result) => {
                    const currentData = ctx.getState().users.data;
                    // Append data for pagination (page > 0), replace for initial load or page 0
                    const newData = payload.page > 0 ? [...currentData, ...result.data] : result.data;

                    ctx.patchState({
                        users: {
                            data: newData,
                            page: result.page,
                            pageSize: result.pageSize,
                            total: result.total,
                            loading: false
                        }
                    });
                }),
                catchError((error) => {
                    ctx.patchState({
                        users: { ...ctx.getState().users, loading: false },
                        error: error.message || 'Failed to load users'
                    });
                    return of(null);
                })
            );
    }

    /**
     * Loads a single user by GID.
     */
    @Action(UserActions.LoadUserByGid)
    public loadUserByGid(ctx: StateContext<UserStateModel>, { gid }: UserActions.LoadUserByGid): Observable<unknown> {
        return this.userService.getUserByGid(gid).pipe(
            tap((user) => {
                ctx.patchState({
                    selectedUser: user
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    error: error.message || 'Failed to load user'
                });
                return of(null);
            })
        );
    }

    /**
     * Creates a new user.
     */
    @Action(UserActions.CreateUser)
    public async createUser(ctx: StateContext<UserStateModel>, { user }: UserActions.CreateUser): Promise<void> {
        try {
            const createdUser = await this.userService.createUser(user);
            const currentUsers = ctx.getState().users.data;
            ctx.patchState({
                users: {
                    ...ctx.getState().users,
                    data: [...currentUsers, createdUser],
                    total: ctx.getState().users.total + 1
                }
            });
        } catch (error: unknown) {
            ctx.patchState({
                error: (error as Error).message || 'Failed to create user'
            });
            throw error;
        }
    }

    /**
     * Updates an existing user.
     */
    @Action(UserActions.UpdateUser)
    public updateUser(ctx: StateContext<UserStateModel>, { user }: UserActions.UpdateUser): Observable<unknown> {
        return this.userService.updateUser(user).pipe(
            tap((updatedUser) => {
                const currentUsers = ctx.getState().users.data;
                const index = currentUsers.findIndex((u) => u.gid === updatedUser.gid);
                if (index !== -1) {
                    const newUsers = [...currentUsers];
                    newUsers[index] = updatedUser;
                    ctx.patchState({
                        users: {
                            ...ctx.getState().users,
                            data: newUsers
                        }
                    });
                }

                // Update selected user if it's the one being updated
                if (ctx.getState().selectedUser?.gid === updatedUser.gid) {
                    ctx.patchState({
                        selectedUser: updatedUser
                    });
                }

                // Update me if it's the current user
                if (ctx.getState().me?.gid === updatedUser.gid) {
                    ctx.patchState({
                        me: updatedUser
                    });
                }
            }),
            catchError((error) => {
                ctx.patchState({
                    error: error.message || 'Failed to update user'
                });
                return of(null);
            })
        );
    }

    /**
     * Deletes a user.
     */
    @Action(UserActions.DeleteUser)
    public deleteUser(ctx: StateContext<UserStateModel>, { gid }: UserActions.DeleteUser): Observable<unknown> {
        return this.userService.deleteUser(gid).pipe(
            tap(() => {
                const currentUsers = ctx.getState().users.data;
                ctx.patchState({
                    users: {
                        ...ctx.getState().users,
                        data: currentUsers.filter((u) => u.gid !== gid),
                        total: ctx.getState().users.total - 1
                    }
                });

                // Clear selected user if it's the one being deleted
                if (ctx.getState().selectedUser?.gid === gid) {
                    ctx.patchState({
                        selectedUser: null
                    });
                }
            }),
            catchError((error) => {
                ctx.patchState({
                    error: error.message || 'Failed to delete user'
                });
                return of(null);
            })
        );
    }

    /**
     * Patches the current user's profile.
     */
    @Action(UserActions.PatchMe)
    public patchMe(ctx: StateContext<UserStateModel>, { patch }: UserActions.PatchMe): Observable<unknown> {
        return this.userService.patchMe(patch).pipe(
            tap((updatedUser) => {
                ctx.patchState({
                    me: updatedUser
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    error: error.message || 'Failed to update profile'
                });
                return of(null);
            })
        );
    }

    /**
     * Loads the current user's settings.
     */
    @Action(UserActions.LoadUserSettings)
    public loadUserSettings(ctx: StateContext<UserStateModel>): Observable<unknown> {
        ctx.patchState({ settingsLoading: true });

        return this.userService.getUserSettings().pipe(
            tap((settings) => {
                ctx.patchState({
                    settings,
                    settingsLoading: false
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    settingsLoading: false,
                    error: error.message || 'Failed to load user settings'
                });
                return of(null);
            })
        );
    }

    /**
     * Updates the current user's settings.
     */
    @Action(UserActions.UpdateUserSettings)
    public updateUserSettings(
        ctx: StateContext<UserStateModel>,
        { settings }: UserActions.UpdateUserSettings
    ): Observable<unknown> {
        return this.userService.updateUserSettings(settings).pipe(
            tap((updatedSettings) => {
                ctx.patchState({
                    settings: updatedSettings
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    error: error.message || 'Failed to update user settings'
                });
                return of(null);
            })
        );
    }
}
